import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatButtonAppearance, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';

import { resolveSignalValue } from '@lvflanders/bar-ng';

import { _BarInteractiveElementBehavior } from '../interactive-element/bar-interactive-element-behavior';
import { BaasActionButtonOptions, BaasActionButtonVariant } from './baas-action-button.types';

/**
 * Enriched action button options with computed matAppearance property.
 * This is used internally after variant normalization.
 */
type BaasEnrichedActionButtonOptions = BaasActionButtonOptions & {
    /** The computed Material button appearance based on the variant. */
    matAppearance: MatButtonAppearance;
};

/** Generates a button or anchor (link) with material button internally. */
@Component({
    selector: 'baas-action-button',
    templateUrl: './baas-action-button.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, RouterLink, MatIcon, MatIconButton, MatButton, MatBadge, MatTooltip, MatProgressSpinner],
    host: { class: 'bar-action-button' }
})
export class BaasActionButton {
    readonly #clickableElementBehavior = inject(_BarInteractiveElementBehavior);

    protected readonly focusableElement? = viewChild<MatIconButton | MatButton>('focusableElement');

    /** Action button item. */
    readonly item = input.required<BaasActionButtonOptions>();
    // NOTE needed for ag grid cells with action buttons to avoid losing the cell borders
    /** Whether to inject a zero whiteline if hidden. */
    readonly zeroWhiteLineIfHidden = input(false);

    /** Computed enriched item with matAppearance property for Material button. */
    protected readonly enrichedItem = computed<BaasEnrichedActionButtonOptions>(() => {
        const realItem = this.item();
        const matAppearance = this.#variantToMatAppearance(realItem.variant);

        return {
            ...realItem,
            matAppearance
        };
    });

    protected readonly disabled = computed(() => resolveSignalValue(this.enrichedItem().disabled));

    protected readonly hidden = computed(() => resolveSignalValue(this.enrichedItem().hidden));

    protected readonly showProgress = computed(() => resolveSignalValue(this.enrichedItem().showProgress ?? false));

    protected readonly spinnerMode = 'indeterminate' as const;
    protected readonly spinnerDiameter = 20;

    focus() {
        this.focusableElement()?.focus();
    }

    execute() {
        this.buttonClicked(undefined);
    }

    protected async buttonClicked(event) {
        if (this.disabled() || this.hidden()) return;
        await this.#clickableElementBehavior.buttonClick(event, this.enrichedItem());
    }

    protected async linkAction(event) {
        const actionResult = this.enrichedItem().action(event);
        // if a promise was given await it
        if (actionResult instanceof Promise) await actionResult;
        // the user returns false meaning we need to stop propagation
        else if (actionResult === false) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    /** Converts variant to Material button appearance. */
    #variantToMatAppearance(variant?: BaasActionButtonVariant): MatButtonAppearance {
        switch (variant) {
            case 'filled':
                return 'filled';
            case 'outlined':
                return 'outlined';
            case 'tonal':
                return 'tonal';
            case 'text':
                return 'text';
            default:
                // Fallback to outlined for any unexpected values
                return 'outlined';
        }
    }
}
